import { AiFeatureKind } from "@/modules/chats/types/chat-message"

export interface HistoryMessage {
  content: string
  contentBeforeAiFeature?: string
  aiFeatures: AiFeatureKind[]
}

export const createInitialHistory = (): HistoryMessage[] => [{ content: "", aiFeatures: [] }]

const getLatestAiFeaturesForCurrentMessage = (
  currentHistory: HistoryMessage[],
  addedAiFeature?: AiFeatureKind,
): AiFeatureKind[] => {
  const currentAiFeatures = currentHistory.at(-1)?.aiFeatures ?? []
  return addedAiFeature && !currentAiFeatures.includes(addedAiFeature)
    ? [...currentAiFeatures, addedAiFeature]
    : [...currentAiFeatures]
}

const getCapturedContentBeforeAiFeature = (
  currentHistory: HistoryMessage[],
  incomingContentBeforeAiFeature?: string,
): string | undefined =>
  incomingContentBeforeAiFeature?.trim()
    ? incomingContentBeforeAiFeature
    : currentHistory.at(-1)?.contentBeforeAiFeature

export interface ChatHistoryStack {
  past: HistoryMessage[][]
  present: HistoryMessage[]
  future: HistoryMessage[][]
}

export type ChatHistoryAction =
  | { type: "append"; content: string; addedAiFeature?: AiFeatureKind; contentBeforeAiFeature?: string }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; present?: HistoryMessage[] }

export const chatHistoryReducer = (stack: ChatHistoryStack, action: ChatHistoryAction): ChatHistoryStack => {
  const { past, present, future } = stack

  switch (action.type) {
    case "append": {
      const appendedMessage: HistoryMessage = {
        content: action.content,
        contentBeforeAiFeature: getCapturedContentBeforeAiFeature(present, action.contentBeforeAiFeature),
        aiFeatures: getLatestAiFeaturesForCurrentMessage(present, action.addedAiFeature),
      }
      return { past: [...past, present], present: [...present, appendedMessage], future: [] }
    }
    case "undo":
      return past.length === 0
        ? stack
        : { past: past.slice(0, -1), present: past[past.length - 1], future: [present, ...future] }
    case "redo":
      return future.length === 0 ? stack : { past: [...past, present], present: future[0], future: future.slice(1) }
    case "reset":
      return { past: [], present: action.present ?? createInitialHistory(), future: [] }
  }
}
